import React from 'react';
import { Alert, Box, Button, Chip, CircularProgress, FormControlLabel, Slider, Stack, Switch, Typography } from '@mui/material';
import { solarTimeLabel } from '../siteplan/solarPosition';

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export interface SolarControlsProps {
  /** 3DS (sun rig) vs 3D (fixed lighting, only the Auto-spin switch is shown). */
  sunlight: boolean;
  loading: boolean;
  validLocation: boolean;
  north: number | null;
  canCalibrateNorth: boolean;
  enabled: boolean; onEnabledChange: (value: boolean) => void;
  rotating: boolean; onRotatingChange: (value: boolean) => void;
  reducedMotion: boolean;
  month: number; onMonthChange: (month: number) => void;
  minutes: number; onMinutesChange: (minutes: number) => void;
  playing: boolean; onPlayingChange: (value: boolean) => void;
  specialPlaying: boolean; onSpecialPlayingChange: (value: boolean) => void;
  speed: number; onSpeedChange: (speed: number) => void;
  compare: boolean; onCompareChange: (value: boolean) => void;
  /** Opens the calibration dialog (only rendered when `canCalibrateNorth`). */
  onCalibrate: () => void;
  /** Status line at the right of the switch row. */
  statusText: string;
  /** Label for the 3D (no sunlight) status line. */
  massingText?: string;
  error?: string;
  /** Extra alert / hint rendered under the switch row. */
  children?: React.ReactNode;
}

/** The bottom control bar shared by the site-plan and floor-plan solar viewers. */
export default function SolarControls(props: SolarControlsProps) {
  const { sunlight, loading, validLocation, north, canCalibrateNorth, enabled, rotating, reducedMotion, month, minutes,
    playing, specialPlaying, speed, compare, statusText, error } = props;
  const sunOn = enabled && north != null && validLocation;
  return <Box sx={{ px: { xs: 1.5, md: 3 }, py: 2, borderTop: '1px solid #29364a', background: 'linear-gradient(110deg,#192638,#131e2c)' }}>
    <Stack direction="row" sx={{ flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
      {sunlight && <FormControlLabel sx={{ mr: 1 }} control={<Switch checked={sunOn}
        disabled={loading || !validLocation || (north == null && !canCalibrateNorth)}
        onChange={(_, value) => {
          if (value && north == null) { if (canCalibrateNorth) props.onCalibrate(); return; }
          props.onEnabledChange(value);
          if (!value) { props.onPlayingChange(false); props.onSpecialPlayingChange(false); }
        }} />} label="Sunlight" />}
      <FormControlLabel sx={{ mr: 1 }} control={<Switch checked={rotating} onChange={(_, v) => props.onRotatingChange(v)} disabled={reducedMotion} />} label="Auto-spin" />
      {sunlight && canCalibrateNorth && <Button size="small" onClick={props.onCalibrate} sx={{ color: '#ffd293' }} disabled={loading}>{north == null ? 'Set north ↗' : 'Calibrate north ↗'}</Button>}
      {sunlight && loading && <CircularProgress size={16} />}
      <Typography sx={{ ml: 'auto', fontSize: 11, color: '#aabccf' }}>{sunlight ? statusText : (props.massingText ?? 'Massing model · fixed lighting')}</Typography>
    </Stack>
    {sunlight && <>
      {!validLocation && <Alert severity="info">Set this project’s map latitude and longitude to enable sunlight.</Alert>}
      {error && <Alert severity="warning" sx={{ mb: 1 }}>{error}</Alert>}
      {props.children}
      <Box sx={{ display: 'flex', gap: 0.5, overflowX: 'auto', py: 1 }}>
        {MONTHS.map((name, i) => <Button key={name} size="small" aria-pressed={month === i} onClick={() => props.onMonthChange(i)} sx={{ minWidth: 44, flex: 1,
          color: month === i ? '#172334' : '#aebfd2', bgcolor: month === i ? '#ffca85' : 'transparent', '&:hover': { bgcolor: month === i ? '#ffdbab' : '#29374b' } }}>{name}</Button>)}
      </Box>
      <Stack direction="row" spacing={2} alignItems="center">
        <Button variant="contained" disabled={!sunOn} onClick={() => { props.onSpecialPlayingChange(false); props.onPlayingChange(!playing); }}
          sx={{ bgcolor: '#efb76f', color: '#142034', minWidth: 88 }}>{playing ? 'Ⅱ Pause' : '▶ Play'}</Button>
        <Button variant="outlined" disabled={!sunOn || reducedMotion}
          onClick={() => { props.onPlayingChange(false); props.onSpecialPlayingChange(!specialPlaying); }}
          sx={{ color: '#ffd293', borderColor: '#efb76f', minWidth: 128, '&:hover': { borderColor: '#ffd293', bgcolor: 'rgba(239,183,111,.08)' } }}>
          {specialPlaying ? 'Ⅱ Pause Special' : '✦ Play Special'}
        </Button>
        <Slider aria-label="Time of day in Singapore" min={390} max={1170} step={1} value={minutes}
          onChange={(_, v) => { props.onPlayingChange(false); props.onSpecialPlayingChange(false); props.onMinutesChange(v as number); }}
          valueLabelDisplay="auto" valueLabelFormat={solarTimeLabel} sx={{ color: '#efb76f', flex: 1 }} />
        <Button onClick={() => props.onSpeedChange(speed === 1 ? 2 : speed === 2 ? 0.5 : 1)} sx={{ color: '#c1d1e2', minWidth: 40 }}>{speed}×</Button>
      </Stack>
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5, color: '#849bb4' }}>
        <Typography sx={{ fontSize: 10 }}>06:30</Typography>
        <Chip size="small" label="Compare seasons" onClick={() => props.onCompareChange(!compare)} sx={{ color: compare ? '#ffca85' : '#aebfd2', bgcolor: compare ? '#3b3440' : '#223044' }} />
        <Typography sx={{ fontSize: 10 }}>19:30</Typography>
      </Stack>
    </>}
  </Box>;
}

/**
 * Pause / resume the turntable spin, for the overlay panel on the canvas.
 *
 * Drives the same `rotating` state as the Auto-spin switch in the control bar
 * below, so the two always agree — this one just sits where the spinning is,
 * within reach while the viewer is orbiting. Disabled under reduced motion,
 * where the scene never spins in the first place.
 */
export function SolarSpinToggleButton(props: {
  rotating: boolean; onRotatingChange: (value: boolean) => void; reducedMotion: boolean;
}) {
  return <Button size="small" disabled={props.reducedMotion} aria-pressed={props.rotating}
    sx={{ color: '#d4e5f7' }} onClick={() => props.onRotatingChange(!props.rotating)}>
    {props.rotating ? 'Ⅱ Pause' : '▶ Spin'}
  </Button>;
}

/** Shared translucent panel style for overlays on the 3D canvas. */
export const solarPanelSx = { bgcolor: 'rgba(9,17,30,.84)', border: '1px solid rgba(184,211,240,.15)', borderRadius: 2, backdropFilter: 'blur(14px)' };
